import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FontControls, unwrapFamily, BORDER_STYLES } from "@/components/bi/FontStyleControls";
import { Heading1, Rows3, Columns3 } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">{label}</Label>
      {children}
    </div>
  );
}

function ColorRow({ label, value, onChange, testid, fallback = "#ffffff", clearable = true }) {
  return (
    <Row label={label}>
      <span className="flex items-center gap-1">
        {clearable && value && <button data-testid={`${testid}-clear`} onClick={() => onChange("")} className="text-[9px] text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">clear</button>}
        <input data-testid={testid} type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)}
          className="h-6 w-8 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
      </span>
    </Row>
  );
}

function BorderControls({ value = {}, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <input data-testid={`${testid}-color`} type="color" title="Border color" value={value.color || "#dfe5ec"} onChange={(e) => onChange({ color: e.target.value })}
        className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
      <Input data-testid={`${testid}-width`} type="number" min={0} max={6} placeholder="w" value={value.width ?? ""}
        onChange={(e) => onChange({ width: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-12 h-6 text-[10px]" />
      <Select value={value.style || "solid"} onValueChange={(v) => onChange({ style: v })}>
        <SelectTrigger data-testid={`${testid}-style`} className="bg-[color:var(--surface-2)] w-[82px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
        <SelectContent>{BORDER_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Group({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><Icon className="w-3 h-3" />{title}</Label>
      {children}
    </div>
  );
}

const NUM_FORMATS = [
  { v: "", label: "General" }, { v: "number", label: "Number" }, { v: "currency", label: "Currency" }, { v: "percent", label: "Percentage" },
];
const DATE_FORMATS = ["", "yyyy-MM-dd", "dd/MM/yyyy", "MM/dd/yyyy", "dd MMM yyyy", "MMM yyyy"];

// Item 24: independent Header / Value / per-Column formatting for the Data Table.
export function TableFormatEditor({ opts, patchOpts, cols }) {
  const t = opts.tableOpts || {};
  const set = (p) => patchOpts({ tableOpts: { ...t, ...p } });
  const hdr = t.header || {};
  const val = t.values || {};
  const setHdr = (p) => set({ header: { ...hdr, ...unwrapFamily(p) } });
  const setVal = (p) => set({ values: { ...val, ...unwrapFamily(p) } });
  const [selCol, setSelCol] = useState("");
  const cs = (t.styles || {})[selCol] || {};
  const setCol = (p) => set({ styles: { ...(t.styles || {}), [selCol]: { ...cs, ...unwrapFamily(p) } } });
  const clearCol = () => { const st = { ...(t.styles || {}) }; delete st[selCol]; set({ styles: st }); };

  return (
    <div className="space-y-2" data-testid="table-format-editor">
      <Group icon={Heading1} title="Header formatting" testid="table-fmt-header">
        <Row label="Show header"><Switch data-testid="table-hdr-show" checked={hdr.show !== false} onCheckedChange={(v) => setHdr({ show: v })} /></Row>
        <FontControls testid="table-hdr" value={hdr} onChange={setHdr} showVAlign />
        <ColorRow label="Background" testid="table-hdr-bg" value={hdr.bg} onChange={(v) => setHdr({ bg: v })} />
        <Row label="Wrap text"><Switch data-testid="table-hdr-wrap" checked={!!hdr.wrap} onCheckedChange={(v) => setHdr({ wrap: v })} /></Row>
        <Row label="Height / padding (px)">
          <span className="flex gap-1">
            <Input data-testid="table-hdr-height" type="number" min={0} placeholder="auto" value={hdr.height ?? ""} onChange={(e) => setHdr({ height: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
            <Input data-testid="table-hdr-padding" type="number" min={0} placeholder="pad" value={hdr.padding ?? ""} onChange={(e) => setHdr({ padding: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
          </span>
        </Row>
        <Row label="Border"><BorderControls testid="table-hdr-border" value={hdr.border || {}} onChange={(p) => setHdr({ border: { ...(hdr.border || {}), ...p } })} /></Row>
        <Row label={`Opacity (${Math.round((hdr.opacity ?? 1) * 100)}%)`}>
          <input data-testid="table-hdr-opacity" type="range" min={20} max={100} step={5} value={Math.round((hdr.opacity ?? 1) * 100)}
            onChange={(e) => setHdr({ opacity: Number(e.target.value) / 100 })} className="w-24 accent-[color:var(--brand-ocean)]" />
        </Row>
      </Group>

      <Group icon={Rows3} title="Value formatting" testid="table-fmt-values">
        <FontControls testid="table-val" value={val} onChange={setVal} showVAlign />
        <ColorRow label="Background" testid="table-val-bg" value={val.bg} onChange={(v) => setVal({ bg: v })} />
        <Row label="Row height / padding (px)">
          <span className="flex gap-1">
            <Input data-testid="table-val-rowheight" type="number" min={0} placeholder="auto" value={val.rowHeight ?? ""} onChange={(e) => setVal({ rowHeight: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
            <Input data-testid="table-val-padding" type="number" min={0} placeholder="pad" value={val.padding ?? ""} onChange={(e) => setVal({ padding: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
          </span>
        </Row>
        <Row label="Border"><BorderControls testid="table-val-border" value={val.border || {}} onChange={(p) => setVal({ border: { ...(val.border || {}), ...p } })} /></Row>
        <ColorRow label="Alternate row color" testid="table-val-altrow" value={val.altRowColor} onChange={(v) => setVal({ altRowColor: v })} fallback="#f3f6fa" />
        <ColorRow label="Row hover color" testid="table-val-hover" value={val.hoverColor} onChange={(v) => setVal({ hoverColor: v })} fallback="#eaf2f8" />
      </Group>

      <Group icon={Columns3} title="Column formatting (overrides)" testid="table-fmt-column">
        <Select value={selCol || "__none__"} onValueChange={(v) => setSelCol(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="table-col-select" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue placeholder="Pick a column" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Pick a column…</SelectItem>
            {cols.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}{(t.styles || {})[c] && Object.keys((t.styles || {})[c]).length > 2 ? " •" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        {selCol && (
          <>
            <FontControls testid="table-col" value={cs} onChange={setCol} showVAlign />
            <ColorRow label="Background" testid="table-col-bg" value={cs.bg} onChange={(v) => setCol({ bg: v })} />
            <Row label="Wrap text"><Switch data-testid="table-col-wrap" checked={!!cs.wrap} onCheckedChange={(v) => setCol({ wrap: v })} /></Row>
            <Row label="Width min/max (px)">
              <span className="flex gap-1">
                <Input data-testid="table-col-minw" type="number" min={0} placeholder="min" value={cs.minWidth ?? ""} onChange={(e) => setCol({ minWidth: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
                <Input data-testid="table-col-maxw" type="number" min={0} placeholder="max" value={cs.maxWidth ?? ""} onChange={(e) => setCol({ maxWidth: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
              </span>
            </Row>
            <Row label="Number format">
              <Select value={cs.format?.type || "__gen__"} onValueChange={(v) => setCol({ format: { ...(cs.format || {}), type: v === "__gen__" ? "" : v } })}>
                <SelectTrigger data-testid="table-col-numfmt" className="bg-[color:var(--surface-2)] w-[110px] h-6 text-[10px]"><SelectValue placeholder="General" /></SelectTrigger>
                <SelectContent>{NUM_FORMATS.map((f) => <SelectItem key={f.v || "gen"} value={f.v || "__gen__"}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Decimals / units">
              <span className="flex gap-1">
                <Input data-testid="table-col-decimals" type="number" min={0} max={6} placeholder="dec" value={cs.format?.decimals ?? ""} onChange={(e) => setCol({ format: { ...(cs.format || {}), decimals: e.target.value === "" ? undefined : Number(e.target.value) } })} className="bg-[color:var(--surface-2)] w-12 h-6 text-[10px]" />
                <Select value={cs.format?.scale || "none"} onValueChange={(v) => setCol({ format: { ...(cs.format || {}), scale: v === "none" ? "" : v } })}>
                  <SelectTrigger data-testid="table-col-units" className="bg-[color:var(--surface-2)] w-[80px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem><SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="k">K</SelectItem><SelectItem value="m">M</SelectItem><SelectItem value="b">B</SelectItem>
                    <SelectItem value="lakh">Lakh</SelectItem><SelectItem value="crore">Crore</SelectItem>
                  </SelectContent>
                </Select>
              </span>
            </Row>
            {cs.format?.type === "currency" && (
              <Row label="Currency">
                <Select value={cs.format?.currency || "USD"} onValueChange={(v) => setCol({ format: { ...(cs.format || {}), currency: v } })}>
                  <SelectTrigger data-testid="table-col-currency" className="bg-[color:var(--surface-2)] w-[90px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{["USD", "EUR", "GBP", "INR", "AED", "SGD", "MYR", "KES", "NGN"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Row>
            )}
            <Row label="Date format">
              <Select value={cs.dateFormat || ""} onValueChange={(v) => setCol({ dateFormat: v === "__off__" ? "" : v })}>
                <SelectTrigger data-testid="table-col-datefmt" className="bg-[color:var(--surface-2)] w-[128px] h-6 text-[10px]"><SelectValue placeholder="Off" /></SelectTrigger>
                <SelectContent>{DATE_FORMATS.map((f) => <SelectItem key={f || "off"} value={f || "__off__"}>{f || "Off"}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Null display">
              <Input data-testid="table-col-null" placeholder="—" value={cs.nullDisplay ?? ""} onChange={(e) => setCol({ nullDisplay: e.target.value })} className="bg-[color:var(--surface-2)] w-20 h-6 text-[10px]" />
            </Row>
            <Button data-testid="table-col-clear-override" size="sm" variant="ghost" className="h-6 w-full text-[10px] text-[color:var(--danger)]" onClick={clearCol}>
              Remove override — restore global formatting
            </Button>
          </>
        )}
        <p className="text-[9px] text-[color:var(--ink-3)]">Column overrides beat Value formatting for that column only.</p>
      </Group>
    </div>
  );
}
